<template>
  <SectionShell id="expression" num="09" kicker="Expression">
    <template #title>The mouth gets the credit.<br />The <span class="hl">eyebrows</span> do<br />the work.</template>
    <template #lede>
      <p>
        An expression is not a mouth shape. It's an agreement between the brows, the
        lids and the mouth — and when they disagree, the face reads as sarcastic,
        creepy or simply wrong. Drive the two sliders below independently and watch a
        grin turn sinister without the smile changing at all.
      </p>
    </template>

    <div class="exp">
      <figure class="exp__fig card">
        <svg viewBox="0 0 360 340" class="exp__svg" role="img" :aria-label="`A face reading as ${label}`">
          <defs>
            <radialGradient id="ex-skin" cx="0.62" cy="0.3" r="0.8">
              <stop offset="0%" stop-color="#e2e7ec" />
              <stop offset="45%" stop-color="#98a0a9" />
              <stop offset="80%" stop-color="#4a515a" />
              <stop offset="100%" stop-color="#1c222a" />
            </radialGradient>
            <filter id="ex-soft" x="-25%" y="-25%" width="150%" height="150%">
              <feGaussianBlur stdDeviation="2.4" />
            </filter>
          </defs>
          <rect width="360" height="340" fill="#0a0d12" />

          <!-- head -->
          <ellipse cx="180" cy="168" rx="112" ry="140" fill="url(#ex-skin)" />

          <!-- brows -->
          <g stroke="#1b2028" stroke-width="9" fill="none" stroke-linecap="round">
            <path :d="browL" />
            <path :d="browR" />
          </g>

          <!-- eyes -->
          <g>
            <path :d="lidL" fill="#e9edf1" />
            <path :d="lidR" fill="#e9edf1" />
            <circle cx="138" :cy="eyeY" :r="13" fill="#39414a" />
            <circle cx="222" :cy="eyeY" :r="13" fill="#39414a" />
            <circle cx="138" :cy="eyeY" r="6" fill="#0a0c10" />
            <circle cx="222" :cy="eyeY" r="6" fill="#0a0c10" />
            <circle cx="142" :cy="eyeY - 4" r="3" fill="#fff" />
            <circle cx="226" :cy="eyeY - 4" r="3" fill="#fff" />
            <path :d="lidL" fill="none" stroke="#141920" stroke-width="3" />
            <path :d="lidR" fill="none" stroke="#141920" stroke-width="3" />
          </g>

          <!-- nose -->
          <path d="M172 178 Q167 206 159 220 Q170 230 180 230 Q190 230 201 220 Q193 206 188 178"
                fill="#5f6771" opacity="0.6" filter="url(#ex-soft)" />
          <ellipse cx="164" cy="224" rx="5.4" ry="3.6" fill="#12161c" />
          <ellipse cx="196" cy="224" rx="5.4" ry="3.6" fill="#12161c" />

          <!-- nose wrinkle: only when the brows crush down -->
          <g :opacity="wrinkle" stroke="#2b323b" stroke-width="2.6" fill="none" stroke-linecap="round">
            <path d="M166 168 Q180 160 194 168" />
            <path d="M170 178 Q180 172 190 178" />
          </g>

          <!-- mouth -->
          <path :d="mouthPath" fill="#1b2027" />
          <path :d="mouthLine" fill="none" stroke="#0b0e13" stroke-width="3" stroke-linecap="round" />

          <!-- cheek folds when the smile is real -->
          <g :opacity="folds" stroke="#3a424b" stroke-width="3" fill="none" stroke-linecap="round">
            <path d="M118 226 Q108 254 124 278" />
            <path d="M242 226 Q252 254 236 278" />
          </g>

          <!-- crow's feet when the eyes join in -->
          <g :opacity="folds * 0.55" stroke="#454d57" stroke-width="1.8" fill="none" stroke-linecap="round">
            <path d="M106 158 L99 153 M106 165 L98 165 M106 172 L99 176" />
            <path d="M254 158 L261 153 M254 165 L262 165 M254 172 L261 176" />
          </g>
        </svg>

        <figcaption class="exp__cap">
          <span class="exp__label">{{ label }}</span>
          <span class="mono">{{ agree ? 'features agree' : 'features disagree' }}</span>
        </figcaption>
      </figure>

      <div class="exp__side">
        <div class="sliders">
          <label>
            <span class="mono">Mouth <i>{{ mouth > 25 ? 'up' : mouth < -25 ? 'down' : 'level' }}</i></span>
            <input type="range" min="-100" max="100" v-model.number="mouth" aria-label="Mouth curve" />
          </label>
          <label>
            <span class="mono">Brows <i>{{ brow > 25 ? 'raised' : brow < -25 ? 'lowered' : 'level' }}</i></span>
            <input type="range" min="-100" max="100" v-model.number="brow" aria-label="Brow position" />
          </label>
          <div class="presets">
            <button v-for="p in presets" :key="p.n" @click="apply(p)">{{ p.n }}</button>
          </div>
        </div>

        <div class="rules">
          <article>
            <h4 class="mono">Happy pulls up, sad pulls down</h4>
            <p>
              On a cheerful face every line in it travels upward: brows arch, lids open,
              cheeks rise, mouth corners lift, the folds beside the nose deepen. On an
              unhappy face every one of those reverses. Draw the whole face in one
              direction or nothing reads.
            </p>
          </article>
          <article>
            <h4 class="mono">Disagreement has a name</h4>
            <p>
              Keep the smile and drop the brows and you get a leer. Keep the frown and
              raise the brows and you get pleading. This isn't a bug in your drawing —
              it's a lever. Every subtle expression you admire is built by making two
              features disagree by a controlled amount.
            </p>
          </article>
          <article>
            <h4 class="mono">Surprise is a lid, not a mouth</h4>
            <p>
              Whenever the brows shoot up and drag the lids open, you see white
              <em>above</em> the iris as well as below it. That band of sclera is the whole
              signal — a wide-open mouth without it just looks like yawning.
            </p>
          </article>
          <article>
            <h4 class="mono">Approach it as shapes anyway</h4>
            <p>
              An extreme expression feels harder to draw, but it isn't: it's still just
              shapes, darks and lights inside boxes. Grid it, turn it upside down, copy
              the wedges. If you get the shapes right, you get the face right.
            </p>
          </article>
        </div>
      </div>
    </div>
  </SectionShell>
</template>

<script setup>
import { ref, computed } from 'vue'
import SectionShell from '../ui/SectionShell.vue'

const mouth = ref(60)
const brow = ref(45)

const m = computed(() => mouth.value / 100)
const b = computed(() => brow.value / 100)

const eyeY = computed(() => 152 - b.value * 2)

/* brows: outer end moves a little, inner end moves a lot */
const browL = computed(() => {
  const outer = 118 - b.value * 8
  const inner = 118 - b.value * 20
  const ctrl = Math.min(outer, inner) - 8 - b.value * 4
  return `M104 ${outer} Q136 ${ctrl} 166 ${inner}`
})
const browR = computed(() => {
  const outer = 118 - b.value * 8
  const inner = 118 - b.value * 20
  const ctrl = Math.min(outer, inner) - 8 - b.value * 4
  return `M256 ${outer} Q224 ${ctrl} 194 ${inner}`
})

/* lids open as the brows rise */
const open = computed(() => 13 + b.value * 6)
const lidL = computed(() => {
  const y = eyeY.value
  const o = open.value
  return `M112 ${y} Q138 ${y - o} 164 ${y} Q138 ${y + o * 0.85} 112 ${y}Z`
})
const lidR = computed(() => {
  const y = eyeY.value
  const o = open.value
  return `M196 ${y} Q222 ${y - o} 248 ${y} Q222 ${y + o * 0.85} 196 ${y}Z`
})

/* Mouth: the corners lift while the centre sinks — that, and not a smiley-face
   arc, is what a real smile does. Quadratic control points are solved so the
   curve's midpoint lands exactly where we want it. */
const corner = computed(() => 254 - m.value * 17)
const centre = computed(() => 254 + m.value * 9)
const ctrlFor = (mid) => 2 * mid - corner.value

const mouthLine = computed(() => `M136 ${corner.value} Q180 ${ctrlFor(centre.value)} 224 ${corner.value}`)
const mouthPath = computed(
  () =>
    `M136 ${corner.value} Q180 ${ctrlFor(centre.value - 13)} 224 ${corner.value} ` +
    `Q180 ${ctrlFor(centre.value + 15)} 136 ${corner.value}Z`
)

const wrinkle = computed(() => Math.max(0, -b.value - 0.35) * 2.2)
const folds = computed(() => Math.max(0, m.value - 0.3) * 1.6)

const label = computed(() => {
  const M = m.value
  const B = b.value
  if (M > 0.3 && B > 0.3) return 'delighted'
  if (M > 0.3 && B < -0.3) return 'smug / devilish'
  if (M > 0.3) return 'polite smile'
  if (M < -0.3 && B < -0.3) return 'angry'
  if (M < -0.3 && B > 0.3) return 'pleading / about to cry'
  if (M < -0.3) return 'sad'
  if (B > 0.45) return 'surprised'
  if (B < -0.45) return 'sceptical / stern'
  return 'neutral'
})

const agree = computed(() => {
  const M = m.value
  const B = b.value
  if (Math.abs(M) < 0.3 || Math.abs(B) < 0.3) return true
  return M * B > 0
})

const presets = [
  { n: 'neutral', m: 0, b: 0 },
  { n: 'grin', m: 85, b: 60 },
  { n: 'surprised', m: 5, b: 100 },
  { n: 'angry', m: -70, b: -90 },
  { n: 'crying', m: -85, b: 70 },
  { n: 'smug', m: 60, b: -70 }
]
function apply(p) {
  mouth.value = p.m
  brow.value = p.b
}
</script>

<style scoped>
.exp {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  gap: clamp(24px, 3.5vw, 56px);
  align-items: start;
}
.exp__fig { margin: 0; overflow: hidden; position: sticky; top: 92px; background: #0a0d12; }
.exp__svg { display: block; width: 100%; height: auto; }
.exp__svg path, .exp__svg circle, .exp__svg g { transition: d 0.25s, opacity 0.3s; }
.exp__cap {
  border-top: 1px solid var(--edge-soft);
  padding: 12px 14px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.exp__label {
  font-size: 17px;
  color: var(--v5);
  letter-spacing: -0.01em;
}
.exp__cap .mono { color: var(--text-faint); font-size: 9px; }

.sliders { display: flex; flex-direction: column; gap: 20px; margin-bottom: 34px; }
.sliders label span { display: block; color: var(--text-faint); font-size: 10px; margin-bottom: 6px; }
.sliders i { font-style: normal; color: var(--signal); }
.presets { display: flex; gap: 5px; flex-wrap: wrap; margin-top: 4px; }
.presets button { padding: 8px 13px; font-size: 9px; }

.rules { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 1px; background: var(--edge-soft); border: 1px solid var(--edge-soft); }
.rules article { background: rgba(8, 11, 17, 0.8); padding: 22px 20px; }
.rules h4 { color: var(--light); font-size: 9.5px; font-weight: 400; margin-bottom: 10px; }
.rules p { color: var(--text-dim); font-size: 13px; line-height: 1.62; margin: 0; }
.rules em { color: var(--v4); font-style: italic; }

@media (max-width: 980px) {
  .exp { grid-template-columns: 1fr; }
  .exp__fig { position: static; }
}
</style>
